import { message } from 'antd';
import { DROPDOWN_MENU_DIVIDER, Pill, PillShapes } from '@clockout-dev/design-elements';
import {
    PLATFORM_DISPLAY_NAME_UNSET,
    UNASSIGNED_CONNECTION_KEY,
    getConnectionPlatformLabel,
} from '../../common/payrollConnections/payrollConnections.constants';
import {
    BREADCRUMB_ICON,
    ENROLLMENT_STATUS,
    MORE_ACTION_COPY,
    MORE_ACTION_ICONS,
    MORE_ACTION_KEYS,
    MORE_ACTION_TYPES,
    PAYROLL_SOURCE_STATUS_LABELS,
    PAYROLL_SOURCE_STATUS_TONES,
    USER_HEADER_COPY,
} from './user.constants';
import { presentHeader, resolvePayType, resolvePayrollSourceStatus } from './userHeader.utils';
import { NAVBAR_PATHS } from '../../common/layouts/navbar.paths';
import { getBreadcrumbSnapshot, resetBreadcrumb, setBreadcrumb } from '../../../data/breadcrumb.store';
import headerStyles from './elements/styles/user-header.module.scss';

/**
 * The User page header's state reads (KAN-738): it gathers raw values from page
 * state, hands them to the pure rules in `userHeader.utils`, and builds the
 * More actions menu around the page's existing handlers.
 *
 * Mixed into UserController (`Inherit(..., UserHeaderController)`), so the
 * header's components stay presentational — they receive finished text and
 * never decide a rule.
 *
 * What it reads from its host: `this.state` (`consumer`, `syncDetail`,
 * `payrollConnections`, `payrollSourcesOpen`, `funnel`, `funnelLoading`,
 * `isRestricted`, `restrictLoading`), `this.userActionClientId`, the scope checks
 * (`canOpenSso`, `canReconcileAccount`, `canRestrictUser`),
 * `formatMoney` (MoneyController) and the page's action handlers
 * (`handleOpenReconcileModal`, `handleToggleRestrict`), plus
 * `setPayrollSourcesOpen` (UserDelegate). No constructor and no
 * class fields: Inherit copies prototype members only.
 */
class UserHeaderController {
    /**
     * The header's props, ready to spread onto `<UserHeader>`. Gathers the raw
     * values from page state and hands them to `presentHeader`
     * (`userHeader.utils`), which turns them into text.
     *
     * Pay type reads the same sync-report field the old header's
     * workTypeLabel did, so the label does not change; the single
     * connection's own row fills in what the report lacks (employer, pay
     * type) and is the only source of the cadence.
     *
     * @returns {Object}
     */
    buildHeaderModel() {
        const { consumer, syncDetail, payrollSourcesOpen } = this.state;
        const payrolls = this.resolveHeaderPayrolls();
        const soleSource = payrolls.length === 1 ? payrolls[0] : null;
        const header = presentHeader({
            payType: resolvePayType(syncDetail?.report?.salaried) ?? soleSource?.payType ?? null,
            frequency: soleSource?.frequency ?? null,
            payrolls,
            sourceCount: this.resolveHeaderSourceCount(payrolls),
            enrollment: this.resolveHeaderEnrollment(),
        });
        const sourceItems = this.toPayrollSourceMenuItems(header.payrolls);

        return {
            name: consumer?.name || '',
            userId: this.userActionClientId || null,
            primaryEmployer:
                syncDetail?.report?.employer || payrolls.find((source) => source.employer)?.employer || null,
            moreActions: this.toMoreActionMenuItems(this.buildHeaderMoreActions()),
            canOpenSso: this.canOpenSso(),
            ...header,
            payrolls: sourceItems,
            // No rows yet (still loading): the popover stays shut.
            payrollSourcesOpen: Boolean(payrollSourcesOpen) && sourceItems.length > 0,
        };
    }

    /**
     * The page's trail for the app header (Figma «Navbar» breadcrumb):
     * [user icon] › Reports › member name, the earlier steps linking back to
     * Reports.
     *
     * @param {string} [name]
     * @returns {Array<object>}
     */
    buildBreadcrumbSteps(name) {
        return [
            {
                key: 'section',
                icon: <BREADCRUMB_ICON />,
                ariaLabel: USER_HEADER_COPY.BREADCRUMB_ICON_LABEL,
                redirect: NAVBAR_PATHS.reports,
            },
            { key: 'reports', label: USER_HEADER_COPY.BREADCRUMB_SECTION, redirect: NAVBAR_PATHS.reports },
            { key: 'member', label: name || USER_HEADER_COPY.UNKNOWN_NAME },
        ];
    }

    /** The route the trail is keyed by: the same value for publish and clear. */
    getBreadcrumbPathname() {
        return this.props.location?.pathname || window.location.pathname;
    }

    /** Publish the trail to the header for this page's route (breadcrumb.store). */
    publishBreadcrumb(name) {
        setBreadcrumb(this.getBreadcrumbPathname(), this.buildBreadcrumbSteps(name));
    }

    /** Drop this page's trail; leaves one another route published alone. */
    clearBreadcrumb() {
        if (getBreadcrumbSnapshot().pathname === this.getBreadcrumbPathname()) {
            resetBreadcrumb();
        }
    }

    /**
     * The popover rows from `presentHeader` as DropdownMenu items: title,
     * line and a status badge (none for an unknown status).
     *
     * @param {Array<{ key: string, title: string, description: string | null,
     *   status: string | null }>} [sources]
     * @returns {Array<Object>}
     */
    toPayrollSourceMenuItems(sources = []) {
        return sources.map(({ key, title, description, status }) => ({
            key,
            // Informational rows: no hover, press or focus treatment.
            className: headerStyles.sourceRow,
            label: title,
            description,
            extra: status ? (
                <Pill
                    dot
                    tone={PAYROLL_SOURCE_STATUS_TONES[status]}
                    shape={PillShapes.BADGE}
                    label={PAYROLL_SOURCE_STATUS_LABELS[status]}
                />
            ) : null,
        }));
    }

    /**
     * `buildHeaderMoreActions` entries as DropdownMenu items: each `type`
     * becomes its icon, DIVIDER becomes the menu's rule.
     *
     * @param {Array<{ key: string, type: string }>} [actions]
     * @returns {Array<Object>}
     */
    toMoreActionMenuItems(actions = []) {
        return actions.map(({ type, ...item }) => {
            if (type === MORE_ACTION_TYPES.DIVIDER) {
                return { key: item.key, type: DROPDOWN_MENU_DIVIDER };
            }
            const Icon = MORE_ACTION_ICONS[type];
            return { ...item, icon: Icon ? <Icon /> : undefined };
        });
    }

    /**
     * The payroll sources popover's `onOpenChange`. A source row is
     * read-only: clicking it must not dismiss the popover the way picking a
     * menu action would.
     *
     * @param {boolean} next
     * @param {{ source?: string }} [info]
     */
    handlePayrollSourcesOpenChange(next, info) {
        if (!next && info?.source === 'menu') return;
        this.setPayrollSourcesOpen(next);
    }

    /**
     * Popover rows for the header: one per real payroll connection. The
     * `unassigned` bucket (syncs with no account) is not a source a member
     * can be told about, so it is left out.
     *
     * Employer, pay type and frequency are per connection on the endpoint
     * (`employer_name`, `salaried`, `pay_frequency`). The single
     * `report.employer` from sync-status is deliberately NOT fanned across
     * rows — it belongs to one connection.
     *
     * Status comes from the connection's own `state` (resolvePayrollSourceStatus).
     *
     * @returns {Array<{ key: string, provider: string | null, employer: string | null,
     *   payType: string | null, frequency: string | null, status: string | null }>}
     */
    resolveHeaderPayrolls() {
        const { payrollConnections } = this.state;
        if (!Array.isArray(payrollConnections?.data)) {
            return [];
        }

        return payrollConnections.data
            .filter((connection) => connection?.connection_key !== UNASSIGNED_CONNECTION_KEY)
            .map((connection) => {
                const platformLabel = getConnectionPlatformLabel(connection);
                return {
                    key: connection.connection_key,
                    provider: platformLabel === PLATFORM_DISPLAY_NAME_UNSET ? null : platformLabel,
                    employer: connection.employer_name ?? null,
                    payType: resolvePayType(connection.salaried),
                    frequency: connection.pay_frequency ?? null,
                    status: resolvePayrollSourceStatus(connection.state),
                };
            });
    }

    /**
     * How many payroll sources the header claims. The loaded rows win; while
     * they load (or if they failed) the consumer report's `connectionsCount`
     * answers, so "+N" does not flicker in after the panel resolves.
     *
     * @param {Array<Object>} payrolls From `resolveHeaderPayrolls`.
     * @returns {number}
     */
    resolveHeaderSourceCount(payrolls) {
        if (payrolls.length > 0) {
            return payrolls.length;
        }
        const count = Number(this.state.consumer?.connectionsCount);
        return Number.isFinite(count) && count > 0 ? count : 0;
    }

    /**
     * Opt-in / enrollment dates for the header chip (KAN-784).
     *
     * The user report carries `optedInAt`, `enrolledAt` (ISO instants, both
     * nullable) and `enrollmentDatesWarning` (set when the per-user funnel
     * read failed or ran past its budget). With the warning, the funnel
     * response's `enrollment` (same scan, epoch ms) is the fallback: LOADING
     * while `funnelLoading`, ERROR once it has settled without the pair. A payload
     * without the fields (an older service) is `PENDING` and the chip is
     * simply not rendered — never "Enrollment not completed".
     *
     * @returns {{ status: string, optedInAt?: string | null, enrolledAt?: string | null }}
     */
    resolveHeaderEnrollment() {
        const consumer = this.state.consumer || {};
        const hasDates = 'optedInAt' in consumer || 'enrolledAt' in consumer;
        if (!hasDates && !consumer.enrollmentDatesWarning) {
            return { status: ENROLLMENT_STATUS.PENDING };
        }
        if (!consumer.enrollmentDatesWarning) {
            return {
                status: ENROLLMENT_STATUS.READY,
                optedInAt: consumer.optedInAt ?? null,
                enrolledAt: consumer.enrolledAt ?? null,
            };
        }

        // The report answers without the dates when the lake read runs past
        // its budget (a cold scan, often while this page's other lake reads
        // are queued). The funnel this page loads anyway reads the SAME scan
        // and memo with no budget and carries the same pair, so the header
        // waits on it rather than declaring the dates unavailable.
        const { funnel, funnelLoading } = this.state;
        const fromFunnel = funnel?.enrollment;
        if (fromFunnel) {
            const toIso = (ms) => (Number.isFinite(ms) ? new Date(ms).toISOString() : null);
            return {
                status: ENROLLMENT_STATUS.READY,
                optedInAt: toIso(fromFunnel.opted_in_at_ms),
                enrolledAt: toIso(fromFunnel.enrolled_at_ms),
            };
        }
        // Loading only while a funnel request is actually in flight. The report
        // load sets `funnelLoading` before the request starts, so the gap
        // between the two does not flash "unavailable". Once the request
        // settles, anything without the pair (an empty answer, a failure, an
        // admin who cannot read the funnel) is a real "unavailable", never a
        // skeleton that waits forever.
        if (funnelLoading) {
            return { status: ENROLLMENT_STATUS.LOADING };
        }
        return { status: ENROLLMENT_STATUS.ERROR };
    }

    /**
     * The header's More actions menu, as data — Figma «More actions»
     * (296:18399) and «More actions / User blocked» (360:23547). The view only
     * renders it: each entry's `type` picks its icon there, and `onClick` is
     * this page's existing handler.
     *
     * - Copy user ID: the member's Clockout `client_id`; disabled when the
     *   page has none. Never the core-banking `member_id`, which is the
     *   member's sign-in credential and must not reach admins.
     * - Reconcile account: owner-only (`admin:users:reconcile`). Its line
     *   states what it clears — the member's outstanding debt (lifetime, the
     *   figure the KPI cards show as awaiting settlement) — and drops Figma's
     *   "Needs a second approver" (there is none in Phase 1).
     * - Restrict access / Lift restriction: `admin:users:restrict`; type,
     *   label and line flip on `isRestricted`, only restricting is
     *   destructive, and both wait while a restrict call is in flight.
     * - View team actions and Add note are hidden in Figma and not built.
     *
     * @returns {Array<{ key: string, type: string, label?: string,
     *   description?: string, destructive?: boolean, disabled?: boolean,
     *   onClick?: () => void }>}
     */
    buildHeaderMoreActions() {
        const { consumer, isRestricted, restrictLoading } = this.state;
        const items = [
            {
                key: MORE_ACTION_KEYS.COPY_USER_ID,
                type: MORE_ACTION_TYPES.COPY_USER_ID,
                label: MORE_ACTION_COPY.COPY_USER_ID,
                disabled: !this.userActionClientId,
                onClick: () => this.handleCopyUserId(),
            },
        ];

        const gated = [];
        if (this.canReconcileAccount()) {
            const owed = Number(consumer?.awaitingSettlementAmount);
            gated.push({
                key: MORE_ACTION_KEYS.RECONCILE,
                type: MORE_ACTION_TYPES.RECONCILE,
                label: MORE_ACTION_COPY.RECONCILE,
                description:
                    owed > 0
                        ? MORE_ACTION_COPY.RECONCILE_OWED(this.formatMoney(owed))
                        : MORE_ACTION_COPY.RECONCILE_DESCRIPTION,
                onClick: () => this.handleOpenReconcileModal(),
            });
        }
        if (this.canRestrictUser()) {
            gated.push({
                key: MORE_ACTION_KEYS.RESTRICT,
                type: isRestricted ? MORE_ACTION_TYPES.LIFT_RESTRICTION : MORE_ACTION_TYPES.RESTRICT,
                label: isRestricted ? MORE_ACTION_COPY.LIFT_RESTRICTION : MORE_ACTION_COPY.RESTRICT,
                description: isRestricted
                    ? MORE_ACTION_COPY.LIFT_RESTRICTION_DESCRIPTION
                    : MORE_ACTION_COPY.RESTRICT_DESCRIPTION,
                destructive: !isRestricted,
                disabled: Boolean(restrictLoading),
                onClick: () => this.handleToggleRestrict(),
            });
        }

        return gated.length
            ? [...items, { key: MORE_ACTION_KEYS.DIVIDER, type: MORE_ACTION_TYPES.DIVIDER }, ...gated]
            : items;
    }

    /**
     * More actions → Copy user ID (the meta line's ID chip copies the same id
     * through `CopyToClipboard` and reports back via `handleUserIdCopied`).
     * A menu entry cannot host that component, so this writes the clipboard
     * itself; both paths share the same toasts. Copies the member's
     * `client_id` (`userActionClientId`) in full, never the truncated display
     * value and never the core-banking `member_id` (a sign-in credential).
     */
    async handleCopyUserId() {
        const clientId = this.userActionClientId;
        if (!clientId) return;
        try {
            await navigator.clipboard.writeText(clientId);
            this.handleUserIdCopied();
        } catch {
            this.handleUserIdCopyFailed();
        }
    }

    handleUserIdCopied() {
        message.success(USER_HEADER_COPY.USER_ID_COPIED);
    }

    handleUserIdCopyFailed() {
        message.error(USER_HEADER_COPY.USER_ID_COPY_FAILED);
    }
}

export default UserHeaderController;
